/**
 * Finalize Quotation Modal
 * Modal for finalizing the project quotation and moving to contract phase
 * Follows the same format as DesignApprovalModal with package price adjustment
 */

import React, { useState } from 'react';
import { quotationAPI } from '../../services/api';
import { useProjects } from '../../context/ProjectContext';

const FinalizeQuotationModal = ({ isOpen, onClose, projectId, currentPrice, onSuccess }) => {
  const { updatePackagePrice } = useProjects();

  const [notes, setNotes] = useState('');
  const [adjustPrice, setAdjustPrice] = useState(false);
  const [newPrice, setNewPrice] = useState(currentPrice || '');
  const [priceReason, setPriceReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  if (!isOpen) return null;

  // Validate inputs
  const validateInputs = () => {
    if (adjustPrice) {
      if (!newPrice || isNaN(newPrice) || parseFloat(newPrice) <= 0) {
        setError('Please enter a valid price');
        return false;
      }
      if (!priceReason.trim()) {
        setError('Please provide a reason for the price adjustment');
        return false;
      }
    }
    return true;
  };

  // Handle finalization
  const handleFinalize = async () => {
    if (!validateInputs()) return;

    setSubmitting(true);
    setError(null);

    try {
      // Update price if adjusted
      if (adjustPrice && parseFloat(newPrice) !== (parseFloat(currentPrice) || 0)) {
        await updatePackagePrice(projectId, parseFloat(newPrice), priceReason);
      }

      // Finalize quotation with notes
      await quotationAPI.finalize(projectId, notes || null);

      // Success callback
      if (onSuccess) {
        onSuccess({
          notes,
          priceAdjusted: adjustPrice,
          newPrice: adjustPrice ? parseFloat(newPrice) : null
        });
      }

      handleClose();
    } catch (err) {
      console.error('Error finalizing quotation:', err);
      setError(err.message || 'Failed to finalize quotation');
    } finally {
      setSubmitting(false);
    }
  };

  // Reset and close
  const handleClose = () => {
    setNotes('');
    setAdjustPrice(false);
    setNewPrice(currentPrice || '');
    setPriceReason('');
    setError(null);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="bg-purple-600 text-white px-6 py-4 rounded-t-lg flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h2 className="text-xl font-semibold">Finalize Quotation</h2>
          </div>
          <button
            onClick={handleClose}
            disabled={submitting}
            className="text-white hover:text-gray-200 disabled:opacity-50"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body - Scrollable */}
        <div className="p-6 space-y-6 overflow-y-auto flex-1">
          {/* Info box */}
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <p className="text-purple-800">
              You are about to finalize the quotation. This will lock the quotation and move the project to "Ready for Signing" status.
            </p>
          </div>

          {/* Error message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-800 font-medium">Error</p>
              <p className="text-red-600 text-sm mt-1">{error}</p>
            </div>
          )}

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Finalization Notes (Optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={submitting}
              rows={4}
              placeholder="Add any notes about this quotation finalization..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:opacity-50"
            />
          </div>

          {/* Price adjustment toggle */}
          <div className="border-t border-gray-200 pt-6">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={adjustPrice}
                onChange={(e) => setAdjustPrice(e.target.checked)}
                disabled={submitting}
                className="w-5 h-5 text-purple-600 border-gray-300 rounded focus:ring-purple-500 disabled:opacity-50"
              />
              <div>
                <p className="font-medium text-gray-900">Adjust Package Price</p>
                <p className="text-sm text-gray-500">Update the final project price before finalizing</p>
              </div>
            </label>
          </div>

          {/* Price adjustment fields */}
          {adjustPrice && (
            <div className="space-y-4 border border-gray-200 rounded-lg p-4 bg-gray-50">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Current Price
                </label>
                <div className="text-2xl font-bold text-gray-900">
                  RM {(parseFloat(currentPrice) || 0).toLocaleString()}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  New Price (RM) *
                </label>
                <input
                  type="number"
                  value={newPrice}
                  onChange={(e) => setNewPrice(e.target.value)}
                  disabled={submitting}
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:opacity-50"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reason for Price Change *
                </label>
                <textarea
                  value={priceReason}
                  onChange={(e) => setPriceReason(e.target.value)}
                  disabled={submitting}
                  rows={3}
                  placeholder="Explain why the price is being adjusted..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:opacity-50"
                />
              </div>

              {/* Price change indicator */}
              {newPrice && !isNaN(newPrice) && parseFloat(newPrice) !== (parseFloat(currentPrice) || 0) && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Price Change:</span>
                    <span className={`font-semibold ${parseFloat(newPrice) > (parseFloat(currentPrice) || 0) ? 'text-red-600' : 'text-green-600'}`}>
                      {parseFloat(newPrice) > (parseFloat(currentPrice) || 0) ? '+' : ''}
                      RM {(parseFloat(newPrice) - (parseFloat(currentPrice) || 0)).toLocaleString()}
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Warning */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex gap-3">
              <svg className="w-6 h-6 text-yellow-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div>
                <p className="font-medium text-yellow-900">Important</p>
                <p className="text-sm text-yellow-800 mt-1">
                  Please review all quotation items carefully before finalizing. This action cannot be undone.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer - Fixed */}
        <div className="bg-gray-50 border-t border-gray-200 px-6 py-4 flex items-center justify-end gap-3 rounded-b-lg flex-shrink-0">
          <button
            onClick={handleClose}
            disabled={submitting}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleFinalize}
            disabled={submitting}
            className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {submitting ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Finalizing...
              </>
            ) : (
              <>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Finalize Quotation
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FinalizeQuotationModal;
